// EA Benchmark panel — advertised vs realized, per bot per period.
// Surfaces veritas.ea_benchmark / veritas.ea_promise (shipped 2026-05-22,
// tasks/ea-benchmark-findings.md). UI batch 2026-06-11, proposal 1.
//
// Framing (load-bearing — see the findings file): you can FALSIFY a
// performance promise; you cannot confirm one. Max DD only ratchets up with
// time, and for grid/martingale EAs "on-target with low DD" is also the
// signature of a tail not yet hit. So every verdict here is descriptive
// divergence (AHEAD / BEHIND / DD CAP BREACHED / NO CLAIM) — never a
// "verified" stamp. The horizon caveat is rendered permanently in the footer.
//
// Empty state IS the primary state today: ea_promise has no vendor numbers
// yet, so bots render "no claim on file" with an admin-only inline entry form
// (ea_promise write RLS = admin; the CTA is hidden for everyone else).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { veritas } from '@/lib/supabase';
import { useCurrentUser } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Panel, PanelBody, PanelHead, Chip, Chips } from '@/components/ui';

type Grain = 'week' | 'month' | 'quarter' | 'year';
const GRAINS: Grain[] = ['week', 'month', 'quarter', 'year'];

// One row of veritas.ea_benchmark. numeric columns arrive as strings.
type BenchRow = {
  magic: number;
  ea_label: string | null;
  period_start: string;                        // date
  positions: number;
  realized_return_pct: string | number | null;
  promised_return_pct_at_grain: string | number | null;
  return_gap_pct: string | number | null;      // realized − promised; + = ahead
  realized_acct_dd_pct: string | number | null;
  promised_max_dd_pct: string | number | null;
  dd_basis: string | null;
  dd_breached: boolean | null;
  note: string | null;
};

const num = (v: string | number | null | undefined): number | null =>
  v == null ? null : Number(v);

const fmtPctSigned = (v: number | null, signed = true) =>
  v == null ? '—' : `${signed && v > 0 ? '+' : ''}${v.toFixed(2)}%`;

export function EaBenchmarkPanel({
  accountLogin,
  botNames,
}: {
  accountLogin: number;
  // magic → display name from the parent's botPerf/eaIdentities derivation, so
  // this panel never re-derives identity. Falls back to "magic N".
  botNames: Map<number, string>;
}) {
  const { user } = useCurrentUser();
  const [grain, setGrain] = useState<Grain>('week');
  const [rows, setRows] = useState<BenchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Magic whose inline claim editor is open (admin only); null = none.
  const [claimFor, setClaimFor] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await veritas.rpc('ea_benchmark', {
        p_account_login: accountLogin,
        p_magic: null,
        p_grain: grain,
      });
      if (cancelled) return;
      if (r.error) setError(r.error.message);
      else {
        setError(null);
        // Manual fills (magic 0) can never carry a vendor claim — exclude
        // them rather than padding the table with permanent "no claim" noise.
        setRows(((r.data ?? []) as BenchRow[]).filter((row) => row.magic !== 0));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [accountLogin, grain, reloadKey]);

  // Group by magic, periods newest-first. Show the most recent periods per
  // bot (the older history is in /performance; this panel answers "is the
  // claim holding NOW").
  const byBot = useMemo(() => {
    const m = new Map<number, BenchRow[]>();
    for (const r of rows) {
      const list = m.get(r.magic) ?? [];
      list.push(r);
      m.set(r.magic, list);
    }
    for (const list of m.values()) {
      list.sort((a, b) => b.period_start.localeCompare(a.period_start));
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [rows]);

  const MAX_PERIODS = 6;

  const botLabel = useCallback(
    (magic: number, eaLabel: string | null) =>
      eaLabel ?? botNames.get(magic) ?? `magic ${magic}`,
    [botNames],
  );

  if (!loading && byBot.length === 0) return null; // no bot activity at all

  return (
    <Panel>
      <PanelHead
        title={
          <>EA Benchmark{' '}
          <span className="text-text-dim">advertised vs realized — descriptive, never a verification</span></>
        }
        right={
          <Chips>
            {GRAINS.map((g) => (
              <Chip key={g} active={grain === g} onClick={() => setGrain(g)}>
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </Chip>
            ))}
          </Chips>
        }
      />
      <PanelBody>
        {error && (
          <div className="mb-3 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-red">{error}</div>
        )}
        {loading ? (
          <div className="grid h-24 place-items-center text-sm text-text-dim">Loading benchmark…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-[12.5px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-eyebrow text-text-dim">
                  <th className="px-2.5 py-1.5 font-semibold">Bot</th>
                  <th className="px-2.5 py-1.5 font-semibold">Period</th>
                  <th className="px-2.5 py-1.5 font-semibold text-right">Pos.</th>
                  <th className="px-2.5 py-1.5 font-semibold text-right">Actual ret.</th>
                  <th className="px-2.5 py-1.5 font-semibold text-right">Promised</th>
                  <th className="px-2.5 py-1.5 font-semibold text-right">Gap</th>
                  <th className="px-2.5 py-1.5 font-semibold text-right">Acct DD</th>
                  <th className="px-2.5 py-1.5 font-semibold text-right">DD cap</th>
                  <th className="px-2.5 py-1.5 font-semibold">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {byBot.map(([magic, periods]) => {
                  const hasClaim = periods.some((p) => p.promised_return_pct_at_grain != null);
                  const shown = hasClaim ? periods.slice(0, MAX_PERIODS) : periods.slice(0, 1);
                  return shown.map((p, i) => {
                    const realized = num(p.realized_return_pct);
                    const promised = num(p.promised_return_pct_at_grain);
                    const gap = num(p.return_gap_pct);
                    const acctDd = num(p.realized_acct_dd_pct);
                    const ddCap = num(p.promised_max_dd_pct);
                    return (
                      <tr key={`${magic}-${p.period_start}`} className="border-t border-line-soft">
                        <td className="px-2.5 py-2">
                          {i === 0 ? (
                            <span className="font-medium text-text">
                              {botLabel(magic, p.ea_label)}{' '}
                              <span className="font-mono text-[11px] text-text-dim">· {magic}</span>
                            </span>
                          ) : null}
                        </td>
                        <td className="px-2.5 py-2 font-mono text-text-mid">
                          {p.period_start}
                          {p.note && (
                            <span className="ml-1.5 font-sans text-[10.5px] text-text-dim" title={p.note}>
                              ({p.note})
                            </span>
                          )}
                        </td>
                        <td className="px-2.5 py-2 text-right font-mono text-text-mid">{p.positions}</td>
                        <td className={cn('px-2.5 py-2 text-right font-mono',
                          realized == null ? 'text-text-dim' : realized >= 0 ? 'text-green' : 'text-red')}>
                          {fmtPctSigned(realized)}
                        </td>
                        <td className="px-2.5 py-2 text-right font-mono text-text-mid">{fmtPctSigned(promised)}</td>
                        <td className={cn('px-2.5 py-2 text-right font-mono',
                          gap == null ? 'text-text-dim' : gap >= 0 ? 'text-green' : 'text-red')}>
                          {gap == null ? '—' : `${gap >= 0 ? '+' : ''}${gap.toFixed(2)}pp`}
                        </td>
                        <td className={cn('px-2.5 py-2 text-right font-mono',
                          acctDd == null ? 'text-text-dim' : p.dd_breached ? 'text-red' : 'text-text-mid')}
                          title="Account-level deposit-adjusted equity DD within the period — includes manual + other bots on this account. The honest basis: per-bot closed-curve DD is only a lower bound (≈3× understated on grid bots).">
                          {acctDd == null ? '—' : `−${acctDd.toFixed(2)}%`}
                        </td>
                        <td className="px-2.5 py-2 text-right font-mono text-text-mid">
                          {ddCap == null ? '—' : `−${ddCap.toFixed(1)}%${p.dd_basis === 'per_period' ? '/p' : ''}`}
                        </td>
                        <td className="px-2.5 py-2">
                          {promised == null ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="rounded-full border border-line bg-bg-2 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim">
                                no claim on file
                              </span>
                              {user?.isAdmin && (
                                <button
                                  onClick={() => setClaimFor(claimFor === magic ? null : magic)}
                                  className="rounded border border-line bg-bg-2 px-2 py-0.5 text-[10.5px] text-text-mid transition-colors hover:border-green hover:text-text"
                                >
                                  + add vendor claim
                                </button>
                              )}
                            </span>
                          ) : p.dd_breached ? (
                            <span className="rounded-full border border-red/40 bg-red/10 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-red"
                              title="Realized account drawdown in this period exceeded the vendor's advertised max-DD cap. A promise can be falsified — this one was.">
                              dd cap breached
                            </span>
                          ) : gap != null && gap >= 0 ? (
                            <span className="rounded-full border border-green/25 bg-green/10 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-green"
                              title="Realized return met or exceeded the advertised figure this period. Descriptive only — it does NOT verify the claim; the DD tail may simply not have hit yet.">
                              ahead of claim
                            </span>
                          ) : (
                            <span className="rounded-full border border-amber/35 bg-amber/10 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-amber">
                              behind claim
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        )}
        {claimFor != null && user?.isAdmin && (
          <ClaimEditor
            accountLogin={accountLogin}
            magic={claimFor}
            suggestedLabel={botNames.get(claimFor) ?? ''}
            onClose={() => setClaimFor(null)}
            onSaved={() => { setClaimFor(null); setReloadKey((k) => k + 1); }}
          />
        )}
        {/* Horizon caveat — permanent, not a tooltip. The single most important
            sentence on this panel for a martingale/grid fleet. */}
        <p className="mt-3 border-t border-line-soft pt-2.5 text-[11px] leading-relaxed text-text-dim">
          A promise can be <b className="font-medium text-text-mid">falsified</b> (a DD-cap breach is real),
          never <b className="font-medium text-text-mid">confirmed</b>: max drawdown only ratchets up with
          account age, and for grid/martingale EAs smooth on-target months are also the signature of a tail
          not yet hit. "Ahead of claim" describes the past period — it is not a safety rating.
          Account DD includes all activity on the account (manual + other bots).
        </p>
      </PanelBody>
    </Panel>
  );
}

// Inline vendor-claim entry (admin only — ea_promise write RLS enforces this
// server-side; the form is just hidden for non-admins). Captures the claim
// with its basis + source so it stays auditable (findings: claims must carry
// return_period, dd basis, source URL/date).
function ClaimEditor({
  accountLogin,
  magic,
  suggestedLabel,
  onClose,
  onSaved,
}: {
  accountLogin: number;
  magic: number;
  suggestedLabel: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [eaLabel, setEaLabel] = useState(suggestedLabel);
  const [returnPct, setReturnPct] = useState('');
  const [returnPeriod, setReturnPeriod] = useState<Grain>('month');
  const [maxDdPct, setMaxDdPct] = useState('');
  const [ddBasis, setDdBasis] = useState<'all_time' | 'per_period'>('all_time');
  const [scopeAll, setScopeAll] = useState(true); // default row (all accounts on this magic) vs this account only
  const [sourceUrl, setSourceUrl] = useState('');
  const [asOf, setAsOf] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid =
    eaLabel.trim().length > 0 &&
    Number.isFinite(Number(returnPct)) && returnPct.trim() !== '' &&
    Number.isFinite(Number(maxDdPct)) && Number(maxDdPct) >= 0 && maxDdPct.trim() !== '';

  async function save() {
    if (!valid || saving) return;
    setSaving(true);
    const r = await veritas.from('ea_promise').insert({
      ea_label: eaLabel.trim(),
      magic_number: magic,
      account_login: scopeAll ? null : accountLogin,
      promised_return_pct: Number(returnPct),
      return_period: returnPeriod,
      promised_max_dd_pct: Number(maxDdPct),
      max_dd_basis: ddBasis,
      source_url: sourceUrl.trim() || null,
      as_of_date: asOf || null,
    });
    setSaving(false);
    if (r.error) setError(r.error.message);
    else onSaved();
  }

  const field = 'w-full rounded border border-line bg-bg-2 px-2 py-1.5 text-[12.5px] text-text outline-none focus:border-green';
  const label = 'mb-1 block text-[10px] uppercase tracking-eyebrow text-text-dim';

  return (
    <div className="mt-3 rounded-md border border-line bg-bg-2/50 p-3.5">
      <div className="mb-2.5 text-[11px] font-bold uppercase tracking-eyebrow text-text-mid">
        Vendor claim — magic {magic}
      </div>
      {error && (
        <div className="mb-2.5 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-red">{error}</div>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="col-span-2">
          <label className={label}>EA label (preset incl. settings)</label>
          <input className={field} value={eaLabel} onChange={(e) => setEaLabel(e.target.value)}
            placeholder="e.g. Valexos Pro 35k cent (iMO15/MM1.3)" />
        </div>
        <div>
          <label className={label}>Promised return %</label>
          <input className={field} value={returnPct} onChange={(e) => setReturnPct(e.target.value)}
            inputMode="decimal" placeholder="8" />
        </div>
        <div>
          <label className={label}>per</label>
          <select className={field} value={returnPeriod} onChange={(e) => setReturnPeriod(e.target.value as Grain)}>
            {GRAINS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Promised max DD %</label>
          <input className={field} value={maxDdPct} onChange={(e) => setMaxDdPct(e.target.value)}
            inputMode="decimal" placeholder="12" />
        </div>
        <div>
          <label className={label}>DD basis</label>
          <select className={field} value={ddBasis} onChange={(e) => setDdBasis(e.target.value as 'all_time' | 'per_period')}>
            <option value="all_time">all-time cap</option>
            <option value="per_period">per period</option>
          </select>
        </div>
        <div>
          <label className={label}>Source URL</label>
          <input className={field} value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://…" />
        </div>
        <div>
          <label className={label}>Claim as of</label>
          <input className={field} type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 text-[12px] text-text-mid">
          <input type="checkbox" checked={scopeAll} onChange={(e) => setScopeAll(e.target.checked)} />
          Default for ALL accounts running magic {magic} (uncheck = this account only)
        </label>
        <span className="flex-1" />
        <button onClick={onClose}
          className="rounded border border-line bg-bg-2 px-3 py-1.5 text-[11px] uppercase tracking-eyebrow text-text-mid hover:text-text">
          Cancel
        </button>
        <button onClick={save} disabled={!valid || saving}
          className={cn(
            'rounded border px-3 py-1.5 text-[11px] font-bold uppercase tracking-eyebrow transition-colors',
            valid && !saving
              ? 'border-green/40 bg-green/10 text-green hover:bg-green/15'
              : 'cursor-not-allowed border-line bg-bg-2 text-text-dim',
          )}>
          {saving ? 'Saving…' : 'Save claim'}
        </button>
      </div>
    </div>
  );
}
